<template>
  <div class="self-stretch flex flex-col gap-2 px-3 py-2.5 rounded-md bg-surface-2 border border-border-separator"
       data-test="scheduled-earlier-results">
    <div class="self-stretch font-sans text-xs text-text-subtle" data-test="summary">
      {{ summary_label }}
    </div>
    <div class="self-stretch inline-flex items-center gap-2 flex-wrap">
      <button type="button"
              class="font-sans text-xs px-2 py-1 rounded-md cursor-pointer bg-surface-1 border border-border-separator text-text-default"
              :data-test="expanded ? 'collapse' : 'expand'"
              @click="$emit('toggle')">
        {{ expanded ? $t('messaging.scheduledEarlier.collapse') : expand_label }}
      </button>
      <!-- Always present, in both states. The peek reaches at most five of the folded results and the
           thread cannot ask the server for more, so this is the only route to the rest — including
           when the fold is small enough that the peek empties it on screen but history still holds
           runs older than the twenty this thread was served. -->
      <button type="button" data-test="history"
              class="font-sans text-xs px-2 py-1 rounded-md cursor-pointer bg-surface-1 border border-border-separator text-text-link"
              @click="$emit('openHistory')">
        {{ $t('messaging.scheduledEarlier.allResults') }}
      </button>
    </div>
  </div>
</template>

<script>
/**
 * The row that stands in for the older results in the Scheduled tasks thread, and opens them.
 *
 * <b>What its count means.</b> It describes the results being hidden ON THIS SCREEN — never a total.
 * The thread is served its twenty most recent messages and has no way to ask for more, so a figure
 * like "112 results since June" is one this component could not honestly produce. The all-results
 * control is what answers that question, by going somewhere that can.
 *
 * Two states, not a counter: collapsed, or opened to reveal the five most recent of the folded
 * results — fewer when fewer are folded, so the control never offers to show five and produce three.
 * Anything older than the peek is reachable only through full run history, which is why that control
 * is offered in BOTH states rather than standing down once the peek looks exhaustive: "nothing left
 * behind" is only ever true of this screen, and the screen is twenty messages deep at most.
 *
 * Each state says what it is showing. Collapsed states the count and the span the fold covers;
 * expanded states how many of that fold are now on screen and how many the fold held — so a user
 * looking at five results knows there were forty-six, and where the other forty-one live.
 */
export default {
  name: 'ScheduledEarlierResults',
  emits: ['toggle', 'openHistory'],
  props: {
    /** How many results are folded away. */
    count: {
      type: Number,
      required: false,
      default: 0,
    },
    /** Oldest folded day, YYYY-MM-DD. Empty renders a count with no range. */
    fromDay: {
      type: String,
      required: false,
      default: '',
    },
    /** Newest folded day, YYYY-MM-DD. */
    toDay: {
      type: String,
      required: false,
      default: '',
    },
    /** Whether the peek is open. */
    expanded: {
      type: Boolean,
      required: false,
      default: false,
    },
    /** How many stay hidden even with the peek open — see the note on the component. */
    hiddenBeyondPeek: {
      type: Number,
      required: false,
      default: 0,
    },
  },
  computed: {
    /**
     * What the row says about itself, in whichever state it is in.
     *
     * Collapsed: the count and the span the fold covers — the range when both ends are known, and the
     * count alone otherwise, because a result with no usable timestamp should cost the row its dates,
     * not its honesty.
     *
     * Expanded: how many of the fold are now on screen against how many it held, so the peek does not
     * read as the whole of the earlier history.
     */
    summary_label() {
      if (this.expanded)
        return this.$t('messaging.scheduledEarlier.expandedSummary', { count: this.peek_size, total: this.count });

      const range = this.day_range;

      return range
        ? this.$t('messaging.scheduledEarlier.summaryRanged', { count: this.count, range })
        : this.$t('messaging.scheduledEarlier.summary', { count: this.count });
    },

    expand_label() {
      return this.$t('messaging.scheduledEarlier.expand', { count: this.peek_size });
    },

    /**
     * How many the expand control will actually reveal, so the label promises the right number: with
     * three folded results it should not offer to show five.
     */
    peek_size() {
      return Math.min(this.count, this.count - this.hiddenBeyondPeek) || this.count;
    },

    /**
     * The folded span in the viewer's locale, or empty when either end is unknown.
     *
     * The day keys are YYYY-MM-DD in the viewer's own zone already (see dayKeyOf), so they are parsed
     * as local parts rather than handed to Date, which would read a bare date string as UTC and shift
     * it back a day for anyone west of Greenwich.
     */
    day_range() {
      const from = this.localDate(this.fromDay);
      const to = this.localDate(this.toDay);
      if (!from || !to) return '';

      const label = date => date.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
      const fromLabel = label(from);
      const toLabel = label(to);

      // One day folded, or several that all landed on one — say it once rather than "Sep 8 – Sep 8".
      return fromLabel === toLabel ? fromLabel : `${fromLabel} – ${toLabel}`;
    },
  },
  methods: {
    /** Parses a YYYY-MM-DD day key as a LOCAL date. Null for anything malformed. */
    localDate(dayKey) {
      if (!dayKey) return null;

      const parts = String(dayKey).split('-').map(Number);
      if (parts.length !== 3 || parts.some(Number.isNaN)) return null;

      const [year, month, day] = parts;
      const date = new Date(year, month - 1, day);
      return Number.isNaN(date.getTime()) ? null : date;
    },
  },
};
</script>
